import styled, { css } from "styled-components";
import { NavLink as BaseNavLink } from "react-router-dom";
import IconSparkle from "../../../icons/IconSparkle";

export const Container = styled.div`
  display: flex;
  align-items: center;

  .menu-enter {
    opacity: 0;
  }
  .menu-enter-active {
    opacity: 1;
    transition: opacity 200ms;
  }
  .menu-exit {
    opacity: 1;
  }
  .menu-exit-active {
    opacity: 0;
    transition: opacity 200ms;
  }
`;

export const MenuButton = styled.button`
  width: 6rem;
  height: 6rem;
  padding: 0;
  cursor: pointer;

  svg {
    width: 2rem;
    height: 2rem;
    color: ${({ theme }) => theme.styleGuide.colors.gray[800]};
    fill: ${({ theme }) => theme.styleGuide.colors.gray[800]};
  }
`;

export const Menu = styled.div`
  display: flex;
  flex-direction: column;
  width: 28rem;
  background-color: ${({ theme }) => theme.styleGuide.colors.white};
  box-shadow: ${({ theme }) => theme.styleGuide.shadows.overlay};
  transform: translateX(-110%);
  transition: transform 0.2s;
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
`;

export const Header = styled.div`
  height: 6.1rem;
  border-bottom: 0.1rem solid
    ${({ theme }) => theme.styleGuide.colors.gray[400]};
`;

export const Logo = styled.span`
  svg {
    width: 14rem;
    height: 2.4rem;
    fill: ${({ theme }) => theme.styleGuide.colors.gray[800]};
  }
`;

export const CompanyLogo = styled.img`
  max-width: 10.7rem;
  max-height: 3.5rem;
`;

export const MenuContent = styled.div`
  flex-grow: 1;
  overflow: auto;
`;

export const Modules = styled.ul`
  margin: 0;
  padding: 0.8rem 1.6rem;
  list-style: none;
`;

export const ModuleLink = styled(BaseNavLink)`
  display: flex;
  align-items: center;
  font-size: 1.4rem;
  font-weight: 600;
  line-height: 2rem;
  padding: 0.8rem;
  text-align: left;
  text-decoration: none;
  border-radius: 0.4rem;
  color: ${({ theme }) => theme.styleGuide.colors.gray[800]};
  position: relative;

  ${({ theme }) => theme.media.sm`
    font-size: 1.5rem;
  `}

  &:hover {
    background-color: ${({ theme }) => theme.styleGuide.colors.gray[300]};

    svg {
      fill: ${({ theme }) => theme.styleGuide.colors.gray[800]};
    }
  }

  &.active {
    color: ${({ theme }) => theme.styleGuide.colors.gray[900]};

    svg {
      fill: ${({ theme }) => theme.styleGuide.colors.gray[800]};
    }
  }
`;

export const ModuleLinkIcon = styled.span`
  flex-shrink: 0;
  margin-right: 1.2rem;

  svg {
    width: 2rem;
    height: 2rem;
    fill: ${({ theme }) => theme.styleGuide.colors.gray[600]};
  }
`;

export const ModuleLinkText = styled.span`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

export const ModuleAction = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 4rem;
  height: 100%;
  padding: 0;
  cursor: pointer;
  transform: translateY(-50%);
  position: absolute;
  top: 50%;
  right: 0;

  svg {
    width: 2rem;
    height: 2rem;
    margin-right: 0.8rem;
    fill: ${({ theme }) => theme.styleGuide.colors.gray[600]};
    pointer-events: none;
  }
`;

export const ModuleBadge = styled.span`
  display: inline-flex;
  align-items: center;
  height: 1.8rem;
  font-size: 1.4rem;
  line-height: 1.4rem;
  margin-right: 0.8rem;
  padding: 0 0.8rem;
  color: ${({ theme }) => theme.styleGuide.colors.gray[800]};
  background-color: ${({ theme }) => theme.styleGuide.colors.gray[500]};
  border-radius: 2rem;

  ${({ theme }) => theme.media.sm`
    font-size: 1.5rem;
    line-height: 1.5rem;
  `}
`;

export const Module = styled.li`
  display: flex;
  flex-direction: column;

  ${Modules} {
    padding: 0 0 0 3.2rem;

    ${ModuleLink} {
      font-weight: 400;
    }
  }

  // * Special cases for the link because react router
  // * yells at us if we pass anything except what they expect.
  ${({ theme, isEditing, isHidden }) =>
    css`
      ${ModuleLink} {
        padding: ${isEditing && "0.8rem 3.6rem 0.8rem 0.8rem"};
        cursor: ${isEditing && "move"};
        color: ${isHidden && theme.styleGuide.colors.gray[600]};

        &:hover {
          color: ${theme.styleGuide.colors.gray[800]};
        }
      }
    `}

  ${({ theme, isDragging }) =>
    isDragging &&
    css`
      ${ModuleLink} {
        background-color: ${theme.styleGuide.colors.gray[300]};
      }
    `}
`;

export const MenuFooter = styled.div`
  flex-shrink: 0;
  margin-top: auto;
`;

export const Meeting = styled.div`
  padding: 1.6rem 2.4rem;
  border-top: 0.1rem solid ${({ theme }) => theme.styleGuide.colors.gray[400]};
`;

export const Customize = styled.button`
  flex-shrink: 0;
  width: 100%;
  font-size: 1.4rem;
  font-weight: 700;
  line-height: 2rem;
  padding: 1.6rem 2.4rem;
  cursor: pointer;
  text-align: left;
  color: ${({ theme }) => theme.live.colors.primary};
  border-top: 0.1rem solid ${({ theme }) => theme.styleGuide.colors.gray[400]};

  ${({ theme }) => theme.media.sm`
    font-size: 1.5rem;
  `}
`;

export const Feedback = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 100%;
  font-size: 1.4rem;
  font-weight: 600;
  line-height: 2rem;
  padding: 1.6rem 2.4rem;
  cursor: pointer;
  text-align: left;
  color: ${({ theme }) => theme.styleGuide.colors.helix['brand']};
  border-top: 0.1rem solid ${({ theme }) => theme.styleGuide.colors.gray[400]};
  background-color: ${({ theme }) => theme.styleGuide.colors.gray[200]};

  text-decoration: none;

  ${({ theme }) => theme.media.sm`
    font-size: 1.5rem;
  `}
`;

export const Sparkle = styled(IconSparkle)`
  width: 2rem;
  height: 2rem;
  color: #8b5cf6;
  flex-shrink: 0;
`;
